import type { ReactNode } from 'react';
import styles from './Card.module.css';
import { toneClass, type Tone } from './Tone';
import { useToneArea } from './ToneArea';

// One card shape: a white body in a near-black outline, its title in bold ink (ADR 0035,
// title row from ADR 0052). The card still carries the tone of the area it is in
// (DECISIONS-LOG 2026-10-06 "Tone style app-wide") for anything inside that draws in it.
// The hero surface is the single exception, at most one per screen.

interface CardProps {
  readonly title?: string;
  readonly meta?: string;
  readonly action?: ReactNode;
  /** Before the title — a record's avatar (Redesign Group 7, the interview's page). */
  readonly leading?: ReactNode;
  /** The one deep-navy surface per screen. */
  readonly isHero?: boolean;
  /** No padding, for a card whose body manages its own edges — a table, a list. */
  readonly isFlush?: boolean;
  /** What the card is about, where that differs from the area it sits in. */
  readonly tone?: Tone;
  readonly children?: ReactNode;
}

export function Card({
  title,
  meta,
  action,
  leading,
  isHero = false,
  isFlush = false,
  tone,
  children,
}: CardProps): ReactNode {
  const area = useToneArea();
  const classes = [toneClass, styles.card, isHero ? styles.hero : '', isFlush ? styles.flush : '']
    .filter(Boolean)
    .join(' ');

  return (
    <section className={classes} data-tone={isHero ? undefined : (tone ?? area.tone)}>
      {(title !== undefined || action !== undefined) && (
        <header className={styles.header}>
          {leading}
          <div className={styles.headerText}>
            {title !== undefined && <h2 className={styles.title}>{title}</h2>}
            {meta !== undefined && <span className={styles.meta}>{meta}</span>}
          </div>
          {action !== undefined && <div className={styles.action}>{action}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

export interface Metric {
  readonly label: string;
  readonly value: string;
  /** 06-qol §12: every dashboard number drills through to the records behind it. */
  readonly onClick?: () => void;
}

/**
 * KPI figures share one container with hairline dividers rather than scattering
 * into separate boxes — a correction the design system makes deliberately.
 */
export function MetricStrip({ metrics }: { metrics: readonly Metric[] }): ReactNode {
  return (
    <div className={styles.metrics}>
      {metrics.map((metric) =>
        metric.onClick ? (
          <button
            key={metric.label}
            type="button"
            className={`${styles.metric} ${styles.metricClickable}`}
            onClick={metric.onClick}
          >
            <span className={styles.metricValue}>{metric.value}</span>
            <span className={styles.metricLabel}>{metric.label}</span>
          </button>
        ) : (
          <div key={metric.label} className={styles.metric}>
            <span className={styles.metricValue}>{metric.value}</span>
            <span className={styles.metricLabel}>{metric.label}</span>
          </div>
        ),
      )}
    </div>
  );
}
